<!--
  Compact overlapping-initials stack showing "who else is on this batch"
  for a multi-assignee task (`groupId` set — see `coAssigneesFor()`'s doc
  in `task-config.js`). Shared by `TaskCard.vue` (Kanban) and
  `TaskListView.vue` (List) rather than duplicated in both, since the
  rendering (initials circles + a "+N" overflow badge + per-person
  aria labels) is identical in both places — only WHERE it's placed on
  the card/row differs, which stays each caller's own concern.

  Renders nothing at all when `coAssignees` is empty — every caller can
  unconditionally drop this into its template without its own `v-if`.
-->
<template>
  <div v-if="coAssignees.length" class="co-assignee-stack d-inline-flex align-items-center">
    <b-avatar
      v-for="person in visiblePeople"
      :key="person.empId || person.name"
      size="22"
      variant="light-primary"
      class="co-assignee-avatar"
      :text="avatarText(person.name)"
      :aria-label="labelFor(person)"
    />
    <b-avatar
      v-if="overflowCount > 0"
      size="22"
      variant="light-secondary"
      class="co-assignee-avatar"
      :text="`+${overflowCount}`"
      :aria-label="overflowLabel"
    />
  </div>
</template>

<script>
import { BAvatar } from 'bootstrap-vue'
import { avatarText } from '@core/utils/filter'

export default {
  name: 'CoAssigneeAvatars',
  components: { BAvatar },
  props: {
    /** `{ empId, name, status }[]` — see `coAssigneesFor()` in `task-config.js`. `status` may be `null` when only the static creation-time roster (no live sibling data) is available. */
    coAssignees: { type: Array, default: () => [] },
    /** How many individual circles to show before collapsing the rest into a "+N" badge — 3 keeps this compact on both a narrow Kanban card and a table cell. */
    max: { type: Number, default: 3 },
  },
  computed: {
    visiblePeople() {
      return this.coAssignees.slice(0, this.max)
    },
    overflowCount() {
      return Math.max(0, this.coAssignees.length - this.max)
    },
    overflowLabel() {
      return this.coAssignees.slice(this.max).map(person => this.labelFor(person)).join(', ')
    },
  },
  methods: {
    avatarText,
    labelFor(person) {
      return person.status ? `${person.name} — ${person.status}` : `${person.name} (also assigned)`
    },
  },
}
</script>

<style lang="scss" scoped>
// Classic overlapping-avatar-stack look — each circle overlaps the one
// before it by ~1/3 of its width, with a white ring (border) so the
// overlap actually reads as distinct circles rather than a blob,
// matching the same visual convention as this app's other avatar-stack
// SCSS (`@core/scss/base/components/avatar.scss`'s `.avatar-group`) without
// depending on that class's own markup shape (which targets a different,
// non-`b-avatar` avatar component elsewhere in this app).
.co-assignee-avatar {
  border: 2px solid #fff;
  margin-left: -8px;
  font-size: 0.7rem;
  font-weight: 600;

  &:first-child {
    margin-left: 0;
  }

  .dark-layout & {
    border-color: #283046;
  }
}
</style>
